import { type ButtonHTMLAttributes, type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';
import { Spinner } from '~/_shared/design-system/ui/atoms/spinner/spinner';

import './base-button.css';

type BaseButtonSize = 'sm' | 'md' | 'lg' | 'icon';

interface BaseButtonProps
	extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-busy'> {
	children?: ReactNode;
	size?: BaseButtonSize;
	is_loading?: boolean;
}

const BaseButton = ({
	children,
	size = 'md',
	is_loading = false,
	disabled,
	id,
	className,
	type = 'button',
	...rest
}: BaseButtonProps) => {
	// Instance-unique default id: two `<BaseButton>`s on one page must not
	// produce duplicate DOM ids. An explicit `id` prop still wins.
	const auto_id = useId();
	return (
		<button
			{...rest}
			id={id ?? `BaseButton-${auto_id}`}
			type={type}
			aria-busy={is_loading || undefined}
			disabled={disabled || is_loading}
			className={classlist(
				'base-button',
				`base-button--${size}`,
				is_loading && 'base-button--loading',
				className,
			)}
		>
			{is_loading ? (
				<>
					<span aria-hidden='true' className='base-button__spinner'>
						<Spinner />
					</span>
					<span className='base-button__label base-button__label--hidden'>
						{children}
					</span>
				</>
			) : (
				children
			)}
		</button>
	);
};

export type { BaseButtonProps, BaseButtonSize };
export { BaseButton };
